import React, { ChangeEvent, MouseEvent, useState } from 'react';
import styled from '@emotion/styled';
import { LoadingButton } from '@mui/lab';
import { Button, Grid, TextField, Typography } from '@mui/material';

import { Exception } from '@/redux/slices/slice_types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getData } from '@/redux/slices/dataTableSlice';
import { selectCanChangeExceptions } from '@/redux/selectors/authSelectors';
import { useToast } from '@/components/_common/Toast';
import axios from '@/_utils/axios';

import { Modal } from '@/components/_common';
import PolarisIcon from '@/components/_common/PolarisIcon';
import OverflowMenuItem from '@/components/_common/OverflowMenu/OverflowMenuItem';
import DrawerActionButton from '@/components/Exceptions/ActionButtons/DrawerActionButton';

interface ExceptionResolveProps {
  exception: Exception;
  onResolutionSubmitted?: () => void;
  button?: boolean;
  listItem?: boolean;
}

const ExceptionResolve = ({ exception, onResolutionSubmitted, button = false, listItem = false }: ExceptionResolveProps) => {
  const dispatch = useAppDispatch();
  const canChangeExceptions = useAppSelector(selectCanChangeExceptions);
  const [open, setOpen] = useState(false);
  const [resolution, setResolution] = useState('');
  const [hasError, setHasError] = useState(false);
  const { error, success } = useToast();
  const [loading, setIsLoading] = useState(false);
  if (!canChangeExceptions) return null;

  const handleModalOpen = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setOpen(true);
  };

  const handleModalClose = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setResolution('');
    setOpen(false);
  };

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (hasError) {
      setHasError(false);
    }
    setResolution(e.target.value);
  };

  const submitResolution = () => {
    if (!resolution.length) {
      setHasError(true);
      error('Please enter a resolution');
    } else {
      setIsLoading(true);
      axios
        .post(`/monitoring/events/${exception.id}/actions/resolve/`, { status_reason: resolution })
        .then((res) => {
          if (!res.data.success) {
            throw new Error();
          } else {
            success('Resolution submitted');
            setOpen(false);
            if (!onResolutionSubmitted) dispatch(getData());
            if (onResolutionSubmitted) onResolutionSubmitted();
          }
        })
        .catch(() => error('There was a problem resolving the exception.'))
        .then(() => {
          setResolution('');
          setIsLoading(false);
        });
    }
  };

  return (
    <div>
      {button ? (
        <Button
          data-testid="mark-resolved-btn"
          size="small"
          color="default"
          variant="text"
          startIcon={<PolarisIcon icon="check_double" height={16} />}
          onClick={handleModalOpen}
        >
          Mark as Resolved
        </Button>
      ) : listItem ? (
        <DrawerActionButton isLink={false} label="Mark as Resolved" iconName="check_double" onClick={handleModalOpen} />
      ) : (
        <OverflowMenuItem key="history" onClick={handleModalOpen}>
          <PolarisIcon icon="check_double" height={18} /> Mark as Resolved
        </OverflowMenuItem>
      )}
      <StyledModal
        loading={false}
        open={open}
        onClose={(e) => handleModalClose(e)}
        title="Mark as Resolved"
        icon={<PolarisIcon icon="check_double" height={18} />}
      >
        <StyledText data-testid="mark-resolved-modal-content">Are you sure you want to mark this exception as resolved?</StyledText>

        <TextField
          fullWidth
          id="resolution"
          label="Resolution Notes"
          multiline
          minRows={4}
          maxRows={8}
          value={resolution}
          error={hasError}
          onChange={handleInput}
          inputProps={{
            'data-testid': 'resolution-notes-input'
          }}
        />

        <Grid container justifyContent="flex-end" alignItems="center" sx={{ mt: 4 }}>
          <Button data-testid="cancel-resolve-btn" variant="text" color="default" onClick={handleModalClose} sx={{ mr: 2 }}>
            Cancel
          </Button>
          <LoadingButton data-testid="confirm-resolve-btn" color="primary" variant="contained" onClick={submitResolution} loading={loading}>
            Resolve
          </LoadingButton>
        </Grid>
      </StyledModal>
    </div>
  );
};

export default ExceptionResolve;

const StyledModal = styled(Modal)`
  .MuiPaper-root {
    max-width: 450px;
    & label {
      margin-bottom: ${(props) => props.theme.spacing(1)};
    }
  }
`;

const StyledText = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(0, 0, 3)
}));
